import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectCustomerType } from '@econtractor/types';
import * as apiObject from 'api/apiObjects';
import { useToast } from 'context/ToastContext';
import { useObjectCard } from '../../context/ObjectCardProvider';
import useCustomerAiInfo from '../../model/useCustomerAiInfo';
import { mergeCustomerFromAi } from '../../model/customerAiUtils';

const EMPTY_CUSTOMER: ObjectCustomerType = {
    personType: '',
    fullName: '',
    edrpouOrIpn: '',
    vatEnabled: false,
    vatNumber: '',
    iban: '',
    bank: '',
    phone: '',
    email: '',
    legalAddress: '',
    representativeName: '',
    representativePosition: '',
};

type YouControlUsr = {
    status?: string;
    name?: string | { fullName?: string; shortName?: string };
    address?: string;
    contacts?: {
        phone?: string | null;
        additionalPhone?: string | null;
        email?: string | null;
        webSite?: string | null;
        website?: string | null;
        otherContacts?: string | null;
    };
    signers?: Array<{
        name?: string | null;
        role?: string | null;
        description?: string | null;
    }>;
    contractorType?: string;
};

const parseObjectCustomer = (rawValue: unknown): ObjectCustomerType => {
    if (!rawValue) return { ...EMPTY_CUSTOMER };
    if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        return { ...EMPTY_CUSTOMER, ...(rawValue as ObjectCustomerType) };
    }
    if (typeof rawValue !== 'string') return { ...EMPTY_CUSTOMER };

    try {
        const parsed = JSON.parse(rawValue);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? { ...EMPTY_CUSTOMER, ...(parsed as ObjectCustomerType) }
            : { ...EMPTY_CUSTOMER };
    } catch (_e) {
        return { ...EMPTY_CUSTOMER };
    }
};

const normalizeCustomerPayload = (formValues: ObjectCustomerType): ObjectCustomerType => ({
    personType: String(formValues.personType || '').trim(),
    fullName: String(formValues.fullName || '').trim(),
    edrpouOrIpn: String(formValues.edrpouOrIpn || '').trim(),
    vatEnabled: Boolean(formValues.vatEnabled),
    vatNumber: String(formValues.vatNumber || '').trim(),
    iban: String(formValues.iban || '').trim(),
    bank: String(formValues.bank || '').trim(),
    phone: String(formValues.phone || '').trim(),
    email: String(formValues.email || '').trim(),
    legalAddress: String(formValues.legalAddress || '').trim(),
    representativeName: String(formValues.representativeName || '').trim(),
    representativePosition: String(formValues.representativePosition || '').trim(),
});

const pickText = (...values: Array<unknown>): string => {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
};

/** Маппінг сирої відповіді YouControl → поля форми замовника */
const mapYouControlToCustomer = (
    yc: YouControlUsr,
    companyCode: string,
): Partial<ObjectCustomerType> => {
    const code = String(companyCode || '').trim();
    const digits = code.replace(/\D/g, '');

    const fullName =
        typeof yc?.name === 'string'
            ? yc.name.trim()
            : pickText(yc?.name?.fullName, yc?.name?.shortName);

    const firstSigner = Array.isArray(yc?.signers) ? yc.signers[0] : null;
    const contractorType = String(yc?.contractorType || '').toLowerCase();

    let personType: string | undefined;
    if (contractorType.includes('фоп') || contractorType.includes('фізич') || digits.length === 10) {
        personType = 'individual';
    } else if (digits.length === 8 || fullName) {
        personType = 'legal';
    }

    return {
        ...(personType ? { personType } : {}),
        ...(fullName ? { fullName } : {}),
        ...(code ? { edrpouOrIpn: code } : {}),
        ...(pickText(yc?.contacts?.phone, yc?.contacts?.additionalPhone)
            ? { phone: pickText(yc?.contacts?.phone, yc?.contacts?.additionalPhone) }
            : {}),
        ...(pickText(yc?.contacts?.email) ? { email: pickText(yc?.contacts?.email) } : {}),
        ...(pickText(yc?.address) ? { legalAddress: pickText(yc?.address) } : {}),
        ...(pickText(firstSigner?.name) ? { representativeName: pickText(firstSigner?.name) } : {}),
        ...(pickText(firstSigner?.role)
            ? { representativePosition: pickText(firstSigner?.role) }
            : {}),
    };
};

const useObjectTabCustomer = () => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { vObject, fetchObject, setShowSpinner, isEditLocked } = useObjectCard();
    const [formValues, setFormValues] = useState<ObjectCustomerType>({ ...EMPTY_CUSTOMER });
    const [isSaving, setIsSaving] = useState(false);
    const [isYcBusy, setIsYcBusy] = useState(false);

    const readOnly = Boolean(isEditLocked);

    const {
        refInputFile,
        isBusy: isAiBusy,
        uploadProgress,
        phase: aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    } = useCustomerAiInfo({
        onParsed: (parsed) => setFormValues((prev) => mergeCustomerFromAi(prev, parsed)),
    });

    useEffect(() => {
        setFormValues(parseObjectCustomer(vObject?.object?.objectCustomer));
    }, [vObject?.object?.objectCustomer]);

    const handleChange = <K extends keyof ObjectCustomerType>(field: K, value: ObjectCustomerType[K]) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleFetchYouControl = async () => {
        const companyCode = String(formValues.edrpouOrIpn || '').trim();
        if (companyCode.length < 5) {
            showToast(t('customer.youControl.messCodeRequired'), 'warning');
            return;
        }
        if (isYcBusy || isSaving || isAiBusy || readOnly) return;

        setIsYcBusy(true);
        const ycData = await apiObject.getYouControlCompany(companyCode);
        setIsYcBusy(false);

        if (!ycData) {
            showToast(t('customer.youControl.messError'), 'danger');
            return;
        }

        const mapped = mapYouControlToCustomer(ycData as YouControlUsr, companyCode);
        setFormValues((prev) => mergeCustomerFromAi(prev, mapped));
        showToast(t('customer.youControl.messLoaded'), 'success');
    };

    const handleSave = async () => {
        if (!vObject?.object?.ID || readOnly || isSaving) return;

        setIsSaving(true);
        setShowSpinner(true);
        const payload = normalizeCustomerPayload(formValues);
        const res = await apiObject.setCustomer(vObject.object.ID, payload);
        await fetchObject();

        if (res) showToast(t('messCustomerSaved'), 'success');
        else showToast(t('common:messError'), 'danger');

        setIsSaving(false);
    };

    const yesLabel = t('customer.fields.vatYes');
    const noLabel = t('customer.fields.vatNo');
    const ycCodeReady = String(formValues.edrpouOrIpn || '').trim().length >= 5;

    const getPersonTypeLabel = (value?: string) => {
        if (value === 'legal') return t('customer.personTypes.legal');
        if (value === 'individual') return t('customer.personTypes.individual');
        return value || '-';
    };

    return {
        t,
        formValues,
        readOnly,
        isSaving,
        isYcBusy,
        isAiBusy,
        ycCodeReady,
        yesLabel,
        noLabel,
        getPersonTypeLabel,
        handleChange,
        handleFetchYouControl,
        handleSave,
        refInputFile,
        uploadProgress,
        aiPhase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useObjectTabCustomer;
